import { useRef, useState, useEffect } from 'react'
import { motion, useInView } from 'framer-motion'
import ScrollAnimation from '../animations/ScrollAnimation'
import FluidBackground from '../animations/FluidBackground'
import CodeSnippet from '../animations/CodeSnippet'
import CharacterReveal from '../animations/CharacterReveal'
import { getBio, getContent } from '../../services/api'
import type { Bio, Content } from '../../types/database'

const transition = { duration: 0.6, ease: [0.25, 1, 0.5, 1] }
const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function About() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })
  const [hoveredSkill, setHoveredSkill] = useState<number | null>(null)
  const [bio, setBio] = useState<Bio | null>(null)
  const [content, setContent] = useState<Content | null>(null)

  useEffect(() => {
    const loadData = async () => {
      const [bioData, contentData] = await Promise.all([
        getBio(),
        getContent(),
      ])
      setBio(bioData)
      setContent(contentData)
    }
    loadData()
  }, [])

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-background"
    >
      <div className="container mx-auto max-w-7xl relative z-10">
        <ScrollAnimation variant="slideUp" delay={0.2} className="text-center mb-16">
          <motion.span
            className="inline-block px-4 py-2 text-xs font-medium text-accent mb-6 uppercase tracking-widest"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={springConfig}
          >
            About Me
          </motion.span>
                  <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold mb-6 font-display leading-[1.1] tracking-tight">
                    <span className="text-gradient-glow">
                      <CharacterReveal text={bio?.about_title?.split(' ').slice(0, 2).join(' ') || 'Crafting Digital'} delay={0.1} />
                    </span>
                    <br />
                    <span className="text-text-primary">
                      <CharacterReveal text={bio?.about_title?.split(' ').slice(2).join(' ') || 'Experiences'} delay={0.3} />
                    </span>
                  </h2>
                  <p className="text-base sm:text-lg text-text-primary/70 max-w-xl mx-auto leading-relaxed mb-16">
                    {bio?.about_description || "I'm a passionate developer with a focus on creating innovative solutions that blend technical excellence with creative design. With expertise in modern web technologies, I bring ideas to life through clean code and engaging user experiences."}
                  </p>
        </ScrollAnimation>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Skills Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ ...transition, delay: 0.3 }}
            className="neu p-8"
          >
                    <h3 className="text-2xl font-semibold mb-6 text-text-primary">Skills</h3>
                    <div className="space-y-6">
                      {(bio?.skills || [])
                        .sort((a, b) => (b.level || 0) - (a.level || 0)) // Sort by proficiency (highest first)
                        .map((skill, index) => (
                <motion.div
                  key={skill.name}
                  onHoverStart={() => setHoveredSkill(index)}
                  onHoverEnd={() => setHoveredSkill(null)}
                  initial={{ opacity: 0, y: 20 }}
                  animate={
                    isInView
                      ? {
                          opacity: 1,
                          y: 0,
                          scale: hoveredSkill === index ? 1.02 : 1,
                        }
                      : {}
                  }
                  transition={{ delay: 0.4 + index * 0.05 }}
                  className="cursor-pointer"
                >
                  <div className="flex justify-between mb-2">
                    <span className="text-text-primary font-medium">{skill.name}</span>
                    <span className="text-accent font-medium">{skill.level}%</span>
                  </div>
                  <div className="h-2.5 neu rounded-full overflow-hidden relative">
                    <motion.div
                      className="h-full rounded-full relative overflow-hidden"
                      initial={{ width: 0 }}
                      animate={
                        isInView ? { width: `${skill.level}%` } : { width: 0 }
                      }
                      transition={{
                        delay: 0.6 + index * 0.05,
                        duration: 0.8,
                        ease: [0.25, 1, 0.5, 1],
                      }}
                    >
                      <FluidBackground intensity={0.8} />
                      <div className="absolute inset-0 bg-gradient-to-r from-accent to-accent-secondary opacity-80" />
                    </motion.div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Timeline Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ ...transition, delay: 0.4 }}
            className="neu p-8"
          >
                    <h3 className="text-2xl font-semibold mb-6 text-text-primary">Journey</h3>
            <div className="space-y-6">
              {(bio?.timeline || [])
                .sort((a, b) => {
                  // Extract year from string (handles formats like "2020", "2020 - Present", etc.)
                  const getYear = (yearStr: string): number => {
                    const match = yearStr.match(/\d{4}/)
                    return match ? parseInt(match[0], 10) : 0
                  }
                  // Sort by year descending (most recent first)
                  return getYear(b.year || '0') - getYear(a.year || '0')
                })
                .map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.5 }}
                  className="relative"
                >
                  <div className="text-accent font-semibold mb-1">
                    {item.year}
                  </div>
                  <div className="text-text-primary font-semibold text-lg mb-1">
                    {item.title}
                  </div>
                  <div className="text-text-primary/70 text-sm">{item.description}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Code Visualization Section */}
          {content && (content.code_example || (content.technologies && content.technologies.length > 0)) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...transition, delay: 0.6 }}
              className="neu p-8 mt-12 w-full"
            >
              <h3 className="text-2xl font-semibold mb-6 text-text-primary">
                {content.technologies && content.technologies.length > 0 ? 'Technologies I Use' : 'Code Example'}
              </h3>
              <div className="w-full">
                {content.code_example ? (
                  <CodeSnippet
                    code={content.code_example}
                    language={content.code_language || 'typescript'}
                    animateTyping={true}
                    typingSpeed={15}
                    className="w-full"
                  />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {content.technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 neu text-sm text-text-primary/70 rounded-xl"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  )
}
